import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

/**
 * ⚠️ **As funções deste módulo LANÇAM com a mensagem do backend** (at-69), como
 * `contratoApi` e `termoTemplateApi` já faziam. Antes só `pendencias` tratava
 * erro, e cada tela remontava `e?.response?.data?.message || e?.message` à mão.
 *
 * 🔴 **`buscarPlanoCotacao` é a exceção, e por uma razão de negócio:** o **404
 * dela é fluxo normal** — significa "este tipo de veículo ainda não tem plano", e
 * é assim que a ficha entra em modo de CRIAÇÃO (at-43). Por isso ela carrega o
 * status HTTP no erro, para a ficha distinguir 404 de "não foi possível carregar"
 * (mesmo padrão do `ErroUsuario` da at-68).
 */
export interface ErroPlanoCotacao extends Error {
  status: number | null;
}

const erroComStatus = (error: unknown, fallback: string): ErroPlanoCotacao => {
  const err = new Error(extrairMensagemErro(error, fallback)) as ErroPlanoCotacao;
  err.status = (error as any)?.response?.status ?? null;
  return err;
};

/** Item de uma das três seções do plano (coberturas / assistências / benefícios). */
export interface ItemPlano {
  descricao: string;
  detalhe?: string;
}

/** Linha da listagem da tela de administração. */
export interface PlanoCotacaoResumo {
  codigo: string;
  nome: string;
  descricaoTipoVeiculo: string | null;
  /** false = o código sumiu por completo do espelho do SGA (a tela avisa). */
  tipoVeiculoConhecido: boolean;
  /** false = o tipo existe no espelho, mas o SGA o marcou INATIVO (at-44). */
  tipoVeiculoAtivo: boolean;
  ordem: number;
  ativo: boolean;
  totalCoberturas: number;
  totalAssistencias: number;
  totalBeneficios: number;
  atualizadoEm: string;
}

/** Tipo de veículo ativo no SGA que ainda não tem plano — a cotação dele falha com 400. */
export interface TipoSemPlano {
  codigo: string;
  descricao: string | null;
}

export interface ListaPlanosCotacao {
  planos: PlanoCotacaoResumo[];
  semPlano: TipoSemPlano[];
}

export interface PlanoCotacaoDetalhe {
  id: string;
  codigo: string;
  nome: string;
  coberturas: ItemPlano[];
  assistencias: ItemPlano[];
  beneficios: ItemPlano[];
  ordem: number;
  ativo: boolean;
  descricaoTipoVeiculo: string | null;
  atualizadoEm: string;
}

export interface UpsertPlanoCotacao {
  nome: string;
  coberturas: ItemPlano[];
  assistencias: ItemPlano[];
  beneficios: ItemPlano[];
  ativo?: boolean;
}

export interface PlanoCotacaoHistorico {
  id: string;
  codigo: string;
  snapshotAnterior: {
    nome?: string;
    coberturas?: ItemPlano[];
    assistencias?: ItemPlano[];
    beneficios?: ItemPlano[];
    ativo?: boolean;
  };
  editadoPorId: string | null;
  criadoEm: string;
}

export const listarPlanosCotacao = async (): Promise<ListaPlanosCotacao> => {
  try {
    const { data } = await http.get('/plano-cotacao');
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao carregar os planos de cotação'));
  }
};

/** ⚠️ 404 é FLUXO (tipo ainda sem plano) — ver o aviso no topo do arquivo. */
export const buscarPlanoCotacao = async (codigo: string): Promise<PlanoCotacaoDetalhe> => {
  try {
    const { data } = await http.get(`/plano-cotacao/${codigo}`);
    return data;
  } catch (error) {
    throw erroComStatus(error, 'Erro ao carregar o plano de cotação');
  }
};

export const salvarPlanoCotacao = async (
  codigo: string,
  dados: UpsertPlanoCotacao,
): Promise<{ plano: PlanoCotacaoDetalhe; criado: boolean }> => {
  try {
    const { data } = await http.put(`/plano-cotacao/${codigo}`, dados);
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao salvar o plano'));
  }
};

export const historicoPlanoCotacao = async (
  codigo: string,
): Promise<PlanoCotacaoHistorico[]> => {
  try {
    const { data } = await http.get(`/plano-cotacao/${codigo}/historico`);
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao carregar o histórico'));
  }
};

/**
 * Tipos de veículo ativos sem plano — alimenta o PendenciasBanner.
 * Nunca lança: uma falha aqui não pode derrubar o banner nem a tela que o monta.
 */
export const pendenciasPlanosCotacao = async (): Promise<{
  tiposSemPlano: number;
  codigos: string[];
}> => {
  try {
    const { data } = await http.get('/plano-cotacao/pendencias');
    return data;
  } catch {
    return { tiposSemPlano: 0, codigos: [] };
  }
};
